// VendorAllCategoriesPrompt — Odia Yes/No dialog asked before the user opens
// the standard "Add Vendor" form. When the same person supplies every
// category (Grocery / Vegetable / Egg / Fuel / Ladoo / Other) the user picks
// "Yes", fills the vendor details ONCE, and we auto-create six vendor rows
// (one per category) with identical data.
//
// Flow:
//   1. User clicks "Add Vendor"
//   2. This prompt opens → "Is this vendor for ALL categories?" [Yes/No]
//      - YES → opens the standard form WITHOUT the Category dropdown,
//              then on Save, broadcasts the same record across all 6
//              categories via parallel `api.upsert` calls.
//      - NO  → opens the standard form normally (user picks one Category
//              and adds them one-by-one).
//
// Implementation note: the actual form is rendered by MasterCrud — we just
// surface this question above it and pass back the user's choice so the
// surrounding setup page can drive the bulk-create when they pick YES.

import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, X, Info } from "lucide-react";
import { ALL_VENDOR_CATEGORIES, VENDOR_CATEGORY_LABELS_ODIA } from "@/lib/setupGuide";

export default function VendorAllCategoriesPrompt({ open, onChoose, onCancel }) {
  const [busy, setBusy] = useState(false);

  const choose = async (allCats) => {
    if (busy) return;
    setBusy(true);
    try {
      await onChoose(allCats);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel?.()}>
      <DialogContent
        data-testid="vendor-all-categories-prompt"
        className="max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-md overflow-y-auto p-5 sm:p-6"
      >
        <DialogHeader>
          <DialogTitle className="text-base sm:text-lg">
            🛒 ସମସ୍ତ Category ପାଇଁ Vendor?
          </DialogTitle>
          <DialogDescription className="text-sm">
            ଏହି ବ୍ୟବସାୟୀ କ'ଣ ସବୁ category (Grocery, Vegetable, Egg, Fuel, Ladoo, Other) ପାଇଁ ସମାନ?
          </DialogDescription>
        </DialogHeader>

        <div className="my-4 rounded-xl border border-moss/30 bg-moss/5 p-3 text-xs text-ink-900 sm:text-sm" data-testid="vendor-all-cats-info">
          <p className="flex items-start gap-2">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-moss" />
            <span>
              <b>YES</b> ବାଛିଲେ — ଆପଣ ଥରେ form ପୂରଣ କଲେ ଆମେ ସ୍ୱୟଂଚାଳିତ ଭାବେ ସବୁ <b>{ALL_VENDOR_CATEGORIES.length}</b> category ପାଇଁ ତାହାକୁ save କରିଦେବୁ।
            </span>
          </p>
        </div>

        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 sm:text-sm">
          <p>
            <b>NO</b> ବାଛିଲେ — ଆପଣ ପ୍ରତ୍ୟେକ category ପାଇଁ ଅଲଗା ଅଲଗା ବ୍ୟବସାୟୀ ଯୋଡ଼ିବାକୁ ପଡିବ (ଇଂରାଜୀ ଓ ଓଡ଼ିଆ ଉଭୟ ଭାଷାରେ)।
          </p>
          <ul className="mt-2 grid grid-cols-2 gap-1">
            {ALL_VENDOR_CATEGORIES.map((c) => (
              <li key={c} className="rounded-md bg-white/60 px-2 py-1 text-[0.7rem] font-medium">
                • {VENDOR_CATEGORY_LABELS_ODIA[c]}
              </li>
            ))}
          </ul>
        </div>

        <DialogFooter className="mt-4 flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            variant="outline"
            onClick={() => choose(false)}
            disabled={busy}
            data-testid="vendor-all-cats-no"
            className="w-full gap-2 sm:w-auto"
          >
            <X className="h-4 w-4" /> ନା — ଅଲଗା ଅଲଗା ଯୋଡିବି
          </Button>
          <Button
            onClick={() => choose(true)}
            disabled={busy}
            data-testid="vendor-all-cats-yes"
            className="w-full gap-2 bg-clay text-white hover:bg-clay-dark sm:w-auto"
          >
            <Check className="h-4 w-4" /> ହଁ — ସମସ୍ତ category ପାଇଁ
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
